
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import {
  computed,
  defineComponent,
  reactive,
  watch,
  onMounted,
  onBeforeUnmount,
  ref
} from 'vue'
import { useEvent } from '@/provider/event'
import { getEventTerm } from '@/utils/commonMethods' // eslint-disable-line
import LocalNav from '@/components/block/LocalNav.vue'
import LocalNavSide from '@/components/block/LocalNavSide.vue'
import EventUse from '@/components/block/EventUse.vue'
import EventButton from '@/components/block/EventButton.vue'
import EventDetailHospital from '@/components/block/EventDetailHospital.vue'
import EventDetailOnlineLive from '@/components/block/EventDetailOnlineLive.vue'
import EventDetailSeminars from '@/components/block/EventDetailSeminars.vue'
import EventDetailBenefits from '@/components/block/EventDetailBenefits.vue'
import EventDetailHelp from '@/components/block/EventDetailHelp.vue'
import EventPrecautionForJoin from '@/components/block/EventPrecautionForJoin.vue'
import MovieModal from '@/components/block/MovieModal.vue'
import HeadingLv2 from '@/components/elements/HeadingLv2.vue'
import Tag from '@/components/elements/Tag.vue'
import EventCardActive from '@/components/block/EventCardActive.vue'
import { EventDetailHospitalMovie } from '@/types/EventDetail'
import 'dayjs/locale/ja'
dayjs.locale('ja')
dayjs.extend(isBetween)

interface State {
  hospitalMoreHidden: boolean
  modal: boolean
  selectedMovies: EventDetailHospitalMovie[]
  modalTitle: string
  spEventButtonFixed: boolean
}

export default defineComponent({
  name: 'EventsDetail',

  components: {
    EventButton,
    EventDetailHospital,
    EventDetailOnlineLive,
    EventDetailSeminars,
    EventDetailBenefits,
    EventDetailHelp,
    EventPrecautionForJoin,
    // EventDetailReport,
    LocalNav,
    LocalNavSide,
    EventUse,
    EventCardActive,
    Tag,
    HeadingLv2,
    MovieModal
  },

  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const {
      event,
      getEvent,
      getEvents,
      loadEventHospitals,
      recentEvents,
      eventHospitals,
      isNoEntry
    } = useEvent()
    getEvents(4)

    const mainEventLabel = computed(() =>
      event.value.category?.name === 'キャリアガイダンス'
        ? 'キャリアガイダンス'
        : event.value.type
    )
    const eventTypeClass = computed(() => {
      if (mainEventLabel.value === 'キャリアガイダンス') return 'isCareer'
      if (mainEventLabel.value === 'オンライン') return 'isOnline'
      if (mainEventLabel.value === 'オフライン') return 'isOffline'
    })

    const state = reactive<State>({
      hospitalMoreHidden: false,
      modal: false,
      selectedMovies: [],
      modalTitle: '',
      spEventButtonFixed: false
    })

    watch(
      () => route.params.id,
      () => {
        if (route.name === 'EventsDetail') {
          ;(async () => {
            store.commit('showLoading')
            await getEvent(Number(route.params.id))
            store.commit('closeLoading')
            console.log('eventValue:', event.value)

            const registerEvents = {
              name: event.value.title,
              date: getEventTerm(event.value.term_start, event.value.term_end),
              place:
                event.value.type === 'オンライン'
                  ? 'WEB会議ツールによるオンライン参加'
                  : event.value.place_name,
              holding_type: event.value.type === 'オンライン' ? 1 : 2
            }
            console.log('setRegisterEvent', registerEvents)
            store.commit('setRegisterEvent', registerEvents)

            if (
              !event.value.programs_old_layout_flag &&
              !event.value.isArchived &&
              event.value.type === 'オンライン'
            ) {
              loadEventHospitals()
            }
          })()
        }
      },
      { immediate: true }
    )

    const isFinifh = (seminar: any) => {
      const isExpired = dayjs().isAfter(dayjs(seminar.term.end))
      return seminar.finish || isExpired
    }

    // 申し込み済みかどうか判定
    const isSubscribed = computed((): boolean => {
      if (!store.state.isLogin) return false

      const index: number = store.state.userInfo.event_history.findIndex(
        (item: { id: number; event_id: string }) =>
          item.event_id == route.params.id
      )

      return index > -1 ? true : false
    })

    // 配信イベントにzoom配信のものがあるか判定
    const hasZoomUrl = computed(() => {
      let flag = false
      event.value.programs?.forEach((program: any) => {
        if (flag) return
        if (program.item && program.item.length)
          flag = program.item.some((el: any) => el.zoom_url)
      })
      return flag
    })

    const getDateToWeek = (date: string) =>
      dayjs(date).format('YYYY年M月D日（ddd）')
    const getDateToTime = (date: string) =>
      dayjs(date).format('YYYY年M月D日（ddd）H時mm分')
    const getFormattedEventDate = (date: string) =>
      dayjs(date).format('YY/MM/DD')
    const getPlaceInfo = (event: any) =>
      `${event.place_name}(${event.place_prefecture}${event.place_city})`
    const getPlaceAddress = (event: any) =>
      `〒${event.place_zip} ${event.place_prefecture}${event.place_city}${event.place_town}`

    // 旧レイアウトを利用
    const isShowHospitalListLayout = computed((): boolean => {
      if (!Object.keys(event.value).length) return false

      return (
        event.value.programs_old_layout_flag ||
        event.value.isArchived ||
        event.value.type === 'オフライン'
      )
    })

    // 新レイアウトを利用
    const isShowOnlineLiveLayout = computed((): boolean => {
      if (!Object.keys(event.value).length) return false

      return (
        !event.value.programs_old_layout_flag &&
        !event.value.isArchived &&
        event.value.type === 'オンライン'
      )
    })

    // ローカルナビ
    const localNav = computed(() => {
      return [
        { id: 'about', label: 'イベント概要' },
        { id: 'hospital', label: '参加病院一覧', onlyArchive: true },
        { id: 'schedule', label: 'タイムスケジュールと参加病院' },
        { id: 'hospitalOnlineEvent', label: '参加病院', hideArchive: true },
        { id: 'benefits', label: '参加特典' },
        { id: 'seminar', label: 'セミナー紹介' },
        { id: 'use', label: '利用について', hideArchive: true }
      ].filter((nav: any) => {
        if (nav.id === 'benefits' && !event.value.benefits?.length) return false
        if (nav.id === 'seminar' && !event.value.seminars?.length) return false
        if (nav.id === 'schedule' && !isShowOnlineLiveLayout.value) return false
        if (nav.id === 'hospitalOnlineEvent' && !isShowOnlineLiveLayout.value)
          return false
        // アーカイブの時は非表示
        if (nav.hideArchive && event.value.isArchived) return false
        // アーカイブの時だけ表示
        if (nav.onlyArchive && !event.value.isArchived) return false
        return true
      })
    })

    // イベントボタン固定
    const eventButtonWrapper = ref<HTMLElement | null>(null)
    const handleScroll = () => {
      if (!event.isArchived && eventButtonWrapper.value) {
        const scrollPos = window.scrollY
        const buttonPos =
          eventButtonWrapper.value.getBoundingClientRect().top + window.scrollY
        if (scrollPos >= buttonPos) {
          state.spEventButtonFixed = true
        } else {
          state.spEventButtonFixed = false
        }
      }
    }
    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll)
    })

    const toReserve = () => {
      router.push({ path: '/events/reserve' })
    }

    const toEventTop = () => {
      router.push({ path: '/events' })
    }

    const openModal = async (
      movies: EventDetailHospitalMovie[],
      title: string
    ) => {
      const isLogin = await store.dispatch('checkLogin')
      if (!isLogin) return
      state.selectedMovies = movies
      state.modalTitle = title
      state.modal = true
    }

    return {
      isFinifh,
      isSubscribed,
      recentEvents,
      getDateToWeek,
      getDateToTime,
      getFormattedEventDate,
      event,
      eventHospitals,
      isNoEntry,
      toReserve,
      toEventTop,
      openModal,
      localNav,
      eventButtonWrapper,
      state,
      dayjs,
      getPlaceInfo,
      getPlaceAddress,
      isShowHospitalListLayout,
      isShowOnlineLiveLayout,
      hasZoomUrl,
      mainEventLabel,
      eventTypeClass
    }
  }
})
